import React, { useCallback, useEffect, useState } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { SplashScreen } from '@Components/splash/SplashScreen';
import { router } from '@Routes/__root';
import { AppStateTypes } from '@Types/onboarding.types';
import { RouteTrackerProvider } from '@Providers/RouteTrackerProvider/RouteTrackerProvider';
import { StoreSnapShot } from '@Components/devOnly/StoreSnapShot';
import { useGetCountries } from '@Hooks/api/useGetCountries';
import { dataLayerHandler } from '@Services/analytics/analytics.service';
import { GTMEventLabel } from '@Services/analytics/types';
import { loadFonts } from '@Utils/fontLoader';
import { showResumeApplicationModal } from '@Hooks/modalManager/showResumeApplicationModal';
import { buildNavigationStack } from './services';
import { useBoundStore } from './store';
import { initAsyncProcess, initSyncProcess } from './services/init/init-app.service';

const App = () => {
  const { setAppState, appState, setIsLoginProcessFinished } = useBoundStore(
    (state) => state.onboardingSlice
  );
  const [areFontsLoaded, setAreFontsLoaded] = useState<boolean>(false);

  useGetCountries(true, false);

  useEffect(() => {
    loadFonts()
      .then(() => {
        setAreFontsLoaded(true);
      })
      .catch(() => {
        // Even if there's an error, we'll proceed with the app
        setAreFontsLoaded(true);
      });
  }, []);

  const initAppLoadProcess = useCallback(async () => {
    initSyncProcess();
    try {
      const { redirected } = await initAsyncProcess();
      if (redirected) return;
      await buildNavigationStack({ onResume: showResumeApplicationModal });
    } catch (error) {
      console.error('Error initializing app', error);
    }
    setIsLoginProcessFinished(true);
    setAppState(AppStateTypes.READY);
    dataLayerHandler(GTMEventLabel.START);
  }, [setAppState]);

  useEffect(() => {
    // Only initialize the app once fonts are loaded
    if (areFontsLoaded) {
      initAppLoadProcess();
    }
  }, [initAppLoadProcess, areFontsLoaded]);

  return (
    <>
      {(appState === AppStateTypes.PRELOAD || !areFontsLoaded) && <SplashScreen />}
      {areFontsLoaded &&
        (appState === AppStateTypes.READY || appState === AppStateTypes.LOADING) && (
          <RouteTrackerProvider>
            <StoreSnapShot />
            <RouterProvider router={router} />
          </RouteTrackerProvider>
        )}
    </>
  );
};

export default React.memo(App);
